import {
  DSChip,
  DSLinkButton,
} from "@components/design-system/molecules";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AvailableCompanions,
  AvailableCompanionTabs,
  useCompanion,
} from "@hooks/use-companion";
import { useSetCompanionTooltipsDisabled } from "@hooks/use-companion-tooltips-disabled";
import { useCompanionV2 } from "@hooks/use-companion-v2";
import mixpanel from "mixpanel-browser";

import { alert } from "../common/Alert";

const DEFAULT_COMPANION = "otto";

const resolveHeads = (tooltip) => {
  const ids = tooltip.companions?.length
    ? tooltip.companions
    : [DEFAULT_COMPANION];
  return ids
    .map((id) => AvailableCompanions[String(id).toUpperCase()])
    .filter(Boolean);
};

const TooltipCard = ({
  tooltip,
  heads,
  onAction,
  onDismiss,
  onDisable,
  disableLoading,
}) => {
  return (
    <div
      className="relative bg-input text-primary rounded-large p-sm shadow-md shadow-primary border border-secondary flex gap-sm items-start w-96 pointer-events-auto cursor-default"
      data-testid={`companion-tooltip-${tooltip.id}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-input border-l border-b border-secondary rotate-45" />
      {heads.length > 0 && (
        <div className="flex flex-col -space-y-1 shrink-0">
          {heads.map((c) => (
            <img
              key={c.name}
              src={c.headImage}
              alt={c.displayName}
              className="w-8 h-8"
            />
          ))}
        </div>
      )}
      <div className="flex flex-col gap-xs flex-1 text-sm min-w-0">
        <div className="break-words">{tooltip.content}</div>
        {tooltip.actions.length > 0 && (
          <div className="flex flex-wrap gap-xs">
            {tooltip.actions.map((action, i) => (
              <DSChip
                key={`${tooltip.id}-action-${i}`}
                variant="selectable"
                onClick={() => onAction(action)}
                dataTestId={`companion-tooltip-${tooltip.id}-action-${i}`}
              >
                {action.label}
              </DSChip>
            ))}
          </div>
        )}
        <div className="text-xs mt-4">
          <DSLinkButton
            onClick={onDisable}
            disabled={disableLoading}
            dataTestId={`companion-tooltip-${tooltip.id}-disable`}
          >
            Don't show me Companion Tips
          </DSLinkButton>
          <br />
          <small>
            You can turn them back on at any time in your Account
            Settings.
          </small>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="text-secondary hover:text-primary shrink-0"
        aria-label="Dismiss"
        data-testid={`companion-tooltip-${tooltip.id}-dismiss`}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
};

export const CompanionTooltips = () => {
  const { expandSidebar, setActiveTab } = useCompanion();
  const { tooltip, dismissCompanionTooltip, sendMessage } =
    useCompanionV2();
  const { setDisabled, loading: disableLoading } =
    useSetCompanionTooltipsDisabled();

  if (!tooltip) return null;

  const handleAction = async (action) => {
    action.onClick?.();
    dismissCompanionTooltip();
    if (action.message) {
      expandSidebar();
      setActiveTab(AvailableCompanionTabs.chat);
      await sendMessage(action.message);
    }
  };

  const handleDismiss = () => {
    tooltip.onDismiss?.();
    dismissCompanionTooltip();
  };

  const handleDisable = async () => {
    try {
      const updated = await setDisabled(true);
      if (!updated) return;
      mixpanel.track("Updated Account Setting", {
        settingName: "Disable Companion Tooltips",
        settingKey: "disable-companion-tooltips",
        settingValue: true,
        source: "companion-tooltip",
      });
      dismissCompanionTooltip();
    } catch {
      alert(
        "error",
        "Failed to save preference. Please try again later.",
      );
    }
  };

  return (
    <div
      className="absolute top-30 left-full ml-sm -translate-y-1/2 z-popover pointer-events-none"
      data-testid="companion-tooltip"
    >
      <TooltipCard
        tooltip={tooltip}
        heads={resolveHeads(tooltip)}
        onAction={handleAction}
        onDismiss={handleDismiss}
        onDisable={handleDisable}
        disableLoading={disableLoading}
      />
    </div>
  );
};
